import { useEffect } from "react";

/**
 * Hook to inject uikit CSS styles into the DOM
 *
 * IMPORTANT: For the CSS to work, consumers must import it:
 *   import '@cuadra-ai/uikit/styles';
 *
 * This hook provides a fallback that tries to inject styles if they're not already loaded.
 */
export function useInjectStyles(): void {
  useEffect(() => {
    // Check if styles already injected or if CSS is already loaded
    const existingStyle = document.querySelector("style[data-cuadra-uikit]");
    if (existingStyle) {
      return;
    }

    // Check if CSS classes are already available (styles might be loaded via import)
    const testEl = document.createElement("div");
    testEl.className = "cuadra-flex";
    document.body.appendChild(testEl);
    const stylesLoaded = window.getComputedStyle(testEl).display === "flex";
    document.body.removeChild(testEl);

    if (stylesLoaded) {
      // Styles are already loaded, no need to inject
      return;
    }

    // Styles not loaded - try to inject them
    // Note: This is a fallback. The proper way is to import '@cuadra-ai/uikit/styles'
    // Silently attempt to load styles - user should import '@cuadra-ai/uikit/styles'

    // Create a link element to load the CSS
    const link = document.createElement("link");
    link.setAttribute("data-cuadra-uikit", "true");
    link.rel = "stylesheet";
    link.type = "text/css";

    // Try to find the CSS file
    // In development with file: protocol, try relative path
    // In production, it should be in node_modules
    const possiblePaths = [
      "/node_modules/@cuadra-ai/uikit/dist/uikit.css",
      new URL("../dist/uikit.css", import.meta.url).href,
    ];

    let loaded = false;
    for (const path of possiblePaths) {
      link.href = path;
      link.onload = () => {
        loaded = true;
      };
      link.onerror = () => {
        // Try next path
      };
      document.head.appendChild(link);
      if (loaded) break;
    }
  }, []);
}
